import { Building2, Calculator } from "lucide-react";
import { useTheme } from "next-themes";
import { useTranslation } from "react-i18next";

import { Card, CardContent } from "@/components/ui/card";
import type { EnterpriseCatalogEntry } from "@/services/enterpriseCatalogService";
import { formatCatalogAmountMinor } from "@/services/certificatePricingCatalogService";
import { enterpriseLogoUrl, enterpriseDarkLogoUrl } from "@/services/platformEnterprisesService";
import type { ProductGraph, PricingNodeData } from "@gestionalo/enterprise-graph";

function EnterpriseBrandStrip({
  enterprise,
}: {
  enterprise: EnterpriseCatalogEntry["enterprise"];
}): JSX.Element {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const bgColor =
    (isDark
      ? (enterprise?.primaryColorDark ?? enterprise?.primaryColor)
      : enterprise?.primaryColor) ?? "#f4f4f5";
  const hasLogo = Boolean(enterprise?.logoUrl && enterprise?.id);
  const hasDarkLogo = Boolean(enterprise?.logoUrlDark && enterprise?.id);

  return (
    <div
      className="flex items-center gap-2 rounded-t-lg border-b border-black/5 px-4 py-2 dark:border-white/10"
      style={{ backgroundColor: bgColor }}
    >
      {hasLogo ? (
        hasDarkLogo ? (
          <>
            <img
              src={enterpriseLogoUrl(enterprise!.id)}
              alt={enterprise!.name}
              className="h-5 w-auto object-contain dark:hidden"
            />
            <img
              src={enterpriseDarkLogoUrl(enterprise!.id)}
              alt={enterprise!.name}
              className="hidden h-5 w-auto object-contain dark:block"
            />
          </>
        ) : (
          <img
            src={enterpriseLogoUrl(enterprise!.id)}
            alt={enterprise!.name}
            className="h-5 w-auto object-contain dark:[filter:brightness(0)_invert(1)]"
          />
        )
      ) : (
        <>
          <Building2 className="h-4 w-4 text-foreground/70" />
          <span className="text-xs font-medium text-foreground/80">
            {enterprise?.name ?? "Enterprise"}
          </span>
        </>
      )}
    </div>
  );
}

function getLowestRate(graph: ProductGraph | null): number | null {
  if (!graph) return null;
  const pricingNode = graph.nodes.find((n) => n.type === "pricing");
  if (!pricingNode) return null;
  const formula = (pricingNode.data as PricingNodeData).formula;
  if (!formula?.rates?.length) return null;
  const allRates = formula.rates.map((r) => r.annualRatePercent);
  return Math.min(...allRates);
}

function VariablePriceLabel({
  graph,
  t,
}: {
  graph: ProductGraph | null;
  t: ReturnType<typeof useTranslation>["t"];
}): JSX.Element {
  const lowestRate = getLowestRate(graph);
  if (lowestRate != null) {
    const pct = lowestRate.toFixed(2).replace(/\.?0+$/, "");
    return (
      <div className="flex items-center gap-1.5">
        <Calculator className="h-3.5 w-3.5 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          {t("platformEnterprises.fromRate", { rate: pct })}
        </p>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <Calculator className="h-3.5 w-3.5 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">
        {t("platformEnterprises.customQuote")}
      </p>
    </div>
  );
}

export function EnterpriseProductCard({
  entry,
  onClick,
}: {
  entry: EnterpriseCatalogEntry;
  onClick?: () => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();

  return (
    <Card
      className="overflow-hidden border transition-all duration-200 hover:shadow-md hover:border-primary/50 hover:scale-[1.02] cursor-pointer"
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <EnterpriseBrandStrip enterprise={entry.enterprise} />
      <CardContent className="space-y-2 p-4">
        <h3 className="text-base font-semibold text-foreground">
          {entry.displayName}
        </h3>
        {entry.description && (
          <p className="text-sm leading-relaxed text-muted-foreground line-clamp-2">
            {entry.description}
          </p>
        )}
        <div className="border-t pt-2.5">
          {entry.amountMinor > 0 ? (
            <p className="text-base font-bold tracking-tight text-primary">
              {formatCatalogAmountMinor(
                entry.amountMinor,
                entry.currency,
                i18n.language,
              )}
            </p>
          ) : (
            <VariablePriceLabel graph={entry.graph} t={t} />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
